import { useEffect, useState, FormEvent } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import BaseButton from "@/components/common/BaseButton";
import { usePasswordResetStore } from "@/stores/password_reset_store";

const VERIFICATION_SECONDS = 600; // 10분, 백엔드 인증번호 유효 시간과 동일
const NEW_PASSWORD_MIN_LENGTH = 8;

type Step = "email" | "code" | "password" | "done";

interface FindPasswordModalProps {
  open: boolean;
  onClose: () => void;
}

function formatTime(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  const remainSeconds = seconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(remainSeconds).padStart(2, "0")}`;
}

export default function FindPasswordModal({ open, onClose }: FindPasswordModalProps) {
  const [step, setStep] = useState<Step>("email");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [timeLeft, setTimeLeft] = useState(VERIFICATION_SECONDS);

  const { loading, error, requestCode, verifyCode, resetPassword, clearError } = usePasswordResetStore();

  // 모달이 열릴 때마다 상태 초기화
  useEffect(() => {
    if (open) {
      setStep("email");
      setEmail("");
      setCode("");
      setResetToken("");
      setNewPassword("");
      setNewPasswordConfirm("");
      setTimeLeft(VERIFICATION_SECONDS);
      clearError();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // 인증번호 입력 단계에서만 카운트다운 동작
  useEffect(() => {
    if (step !== "code") return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => Math.max(prev - 1, 0));
    }, 1000);

    return () => clearInterval(timer);
  }, [step]);

  const isExpired = step === "code" && timeLeft === 0;

  const handleRequestCode = async () => {
    clearError();
    const result = await requestCode(email);

    if (result.success) {
      setCode("");
      setTimeLeft(VERIFICATION_SECONDS);
      setStep("code");
    }
  };

  const handleEmailSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    await handleRequestCode();
  };

  const handleCodeSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearError();
    const result = await verifyCode(email, code);

    if (result.success && result.resetToken) {
      setResetToken(result.resetToken);
      setStep("password");
    }
  };

  const handlePasswordSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    clearError();
    const result = await resetPassword(resetToken, newPassword, newPasswordConfirm);

    if (result.success) {
      setStep("done");
    }
  };

  const isPasswordTooShort = newPassword.length > 0 && newPassword.length < NEW_PASSWORD_MIN_LENGTH;
  const isPasswordMismatch = newPasswordConfirm.length > 0 && newPassword !== newPasswordConfirm;
  const canResetPassword =
    newPassword.length >= NEW_PASSWORD_MIN_LENGTH && newPassword === newPasswordConfirm;

  const titleByStep: Record<Step, string> = {
    email: "비밀번호 찾기",
    code: "인증번호 입력",
    password: "새 비밀번호 설정",
    done: "비밀번호 재설정 완료",
  };

  const descriptionByStep: Record<Step, string> = {
    email: "가입된 관리자 이메일로 인증번호를 보내드립니다.",
    code: `${email}로 발송된 6자리 인증번호를 입력해주세요.`,
    password: "새로 사용할 비밀번호를 입력해주세요.",
    done: "비밀번호가 재설정되었습니다. 새 비밀번호로 로그인해주세요.",
  };

  return (
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="bg-white border-0">
        <DialogHeader>
          <DialogTitle>{titleByStep[step]}</DialogTitle>
          <DialogDescription>{descriptionByStep[step]}</DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {error && (
            <p className="mb-3 text-sm" style={{ color: "#EF4444" }}>
              {error}
            </p>
          )}

          {step === "email" && (
            <form onSubmit={handleEmailSubmit} className="flex flex-col gap-3">
              <Input
                type="email"
                placeholder="관리자 이메일"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={loading}
                required
              />
              <BaseButton
                type="submit"
                label={loading ? "발송 중..." : "인증번호 발송"}
                color={!email.trim() || loading ? "lightgray" : "red"}
                disabled={!email.trim() || loading}
                borderRadius="8px"
              />
            </form>
          )}

          {step === "code" && (
            <form onSubmit={handleCodeSubmit} className="flex flex-col gap-3">
              <Input
                type="text"
                placeholder="인증번호 6자리"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={loading}
                required
              />
              <div className="flex items-center justify-between">
                <span
                  className="text-sm"
                  style={{ color: isExpired ? "#EF4444" : "var(--Stone-500)" }}
                >
                  {isExpired
                    ? "인증 시간이 만료되었습니다. 인증번호를 재발송해 주세요."
                    : `남은 시간 ${formatTime(timeLeft)}`}
                </span>
                <button
                  type="button"
                  onClick={handleRequestCode}
                  disabled={loading}
                  className="text-sm underline cursor-pointer"
                  style={{ color: "var(--Pudding-500)" }}
                >
                  {loading ? "발송 중..." : "인증번호 재발송"}
                </button>
              </div>
              <BaseButton
                type="submit"
                label={loading ? "확인 중..." : "인증하기"}
                color={!code.trim() || loading || isExpired ? "lightgray" : "red"}
                disabled={!code.trim() || loading || isExpired}
                borderRadius="8px"
              />
            </form>
          )}

          {step === "password" && (
            <form onSubmit={handlePasswordSubmit} className="flex flex-col gap-3">
              <Input
                type="password"
                placeholder="새 비밀번호 (8자 이상)"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={loading}
                required
              />
              <Input
                type="password"
                placeholder="새 비밀번호 확인"
                value={newPasswordConfirm}
                onChange={(e) => setNewPasswordConfirm(e.target.value)}
                disabled={loading}
                required
              />
              {isPasswordTooShort && (
                <span className="text-sm" style={{ color: "#EF4444" }}>
                  비밀번호는 8자 이상이어야 합니다.
                </span>
              )}
              {isPasswordMismatch && (
                <span className="text-sm" style={{ color: "#EF4444" }}>
                  새 비밀번호가 일치하지 않습니다.
                </span>
              )}
              <BaseButton
                type="submit"
                label={loading ? "변경 중..." : "비밀번호 재설정"}
                color={!canResetPassword || loading ? "lightgray" : "red"}
                disabled={!canResetPassword || loading}
                borderRadius="8px"
              />
            </form>
          )}

          {step === "done" && (
            <div className="flex flex-col gap-3">
              <BaseButton
                type="button"
                label="로그인하러 가기"
                color="red"
                onClick={onClose}
                borderRadius="8px"
              />
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
